import * as lsp from 'vscode-languageserver';
import Uri from 'vscode-uri';
import path = require('path');
import { getGoRuntimePath, getBinPathWithPreferredGopath, resolveHomeDir, getInferredGopath } from './go-path';
import cp = require('child_process');
import fs = require('fs');
import os = require('os');
import { Emitter } from 'vscode-jsonrpc';

export interface Thenable<T> {
    then<TResult>(onfulfilled?: (value: T) => TResult | Thenable<TResult>, onrejected?: (reason: any) => TResult | Thenable<TResult>): Thenable<TResult>;
    then<TResult>(onfulfilled?: (value: T) => TResult | Thenable<TResult>, onrejected?: (reason: any) => void): Thenable<TResult>;
}

export class DiagnosticCollection {

    public store = new Map<Uri, Diagnostic[]>();
    private onSetEmitter = new Emitter<lsp.PublishDiagnosticsParams>();

    constructor(readonly name: string) { }

    public clear() {
        this.store.forEach((value, uri) => {
            this.onSetEmitter.fire({
                diagnostics: [],
                uri: uriToStringUri(uri),
            });
        });
        this.store.clear();
    }

    public set(uri: Uri, diagnostics: Diagnostic[]) {
        this.store.set(uri, diagnostics);
        this.onSetEmitter.fire({
            diagnostics: diagnostics,
            uri: uriToStringUri(uri),
        });
    }

    public get(uri: Uri) {
        return this.store.get(uri);
    }
}

export function createDiagnosticCollection(name: string): DiagnosticCollection {
    return new DiagnosticCollection(name);
}

export let errorDiagnosticCollection: DiagnosticCollection;
export let warningDiagnosticCollection: DiagnosticCollection;

errorDiagnosticCollection = createDiagnosticCollection('go-error');
warningDiagnosticCollection = createDiagnosticCollection('go-warning');

const extensionId: string = 'lukehoban.Go';

const extensionVersion = '0.1.0';
const aiKey: string = 'AIF-d9b70cd4-b9f9-4d70-929b-a071c400b217';

export const goKeywords: string[] = [
    'break',
    'case',
    'chan',
    'const',
    'continue',
    'default',
    'defer',
    'else',
    'fallthrough',
    'for',
    'func',
    'go',
    'goto',
    'if',
    'import',
    'interface',
    'map',
    'package',
    'range',
    'return',
    'select',
    'struct',
    'switch',
    'type',
    'var',
];

export const goBuiltinTypes: Set<string> = new Set<string>([
    'bool',
    'byte',
    'complex128',
    'complex64',
    'error',
    'float32',
    'float64',
    'int',
    'int16',
    'int32',
    'int64',
    'int8',
    'rune',
    'string',
    'uint',
    'uint16',
    'uint32',
    'uint64',
    'uint8',
    'uintptr',
]);

export class Diagnostic implements lsp.Diagnostic {
    constructor(public readonly range: lsp.Range, public readonly message: string, public readonly severity: lsp.DiagnosticSeverity) { }
}

export interface SemVersion {
    major: number;
    minor: number;
}

let goVersion: SemVersion = null;
let vendorSupport: boolean = null;
let toolsGopath: string;

export function byteOffsetAt(document, position): number {
    let offset = document.offsetAt(position);
    let text = document.getText();
    return Buffer.byteLength(text.substr(0, offset));
}

export interface Prelude {
    imports: Array<{ kind: string; start: number; end: number; }>;
    pkg: { start: number; end: number; name: string };
}

export function canonicalizeGOPATHPrefix(filename: string): string {
    let gopath: string = getCurrentGoPath();
    if (!gopath) { return filename; }
    let workspaces = gopath.split(path.delimiter);
    let filenameLowercase = filename.toLowerCase();

    // In case of multiple workspaces, find current workspace by checking if current file is
    // under any of the workspaces in $GOPATH
    let currentWorkspace: string = null;
    for (let workspace of workspaces) {
        // In case of nested workspaces, (example: both /Users/me and /Users/me/a/b/c are in $GOPATH)
        // both parent & child workspace in the nested workspaces pair can make it inside the above if block
        // Therefore, the below check will take longer (more specific to current file) of the two
        if (filenameLowercase.substring(0, workspace.length) === workspace.toLowerCase()
            && (!currentWorkspace || workspace.length > currentWorkspace.length)) {
            currentWorkspace = workspace;
        }
    }

    if (!currentWorkspace) { return filename; }
    return currentWorkspace + filename.slice(currentWorkspace.length);
}

/**
 * Gets version of Go based on the output of the command `go version`.
 * Returns null if go is being used from source/tip in which case `go version` will not return release tag like go1.6.3
 */
export function getGoVersion(): Promise<SemVersion> {
    let goRuntimePath = getGoRuntimePath();

    if (!goRuntimePath) {
        console.log('Cannot find "go" binary. Update PATH or GOROOT appropriately');
        return Promise.resolve(null);
    }

    if (goVersion) {
        return Promise.resolve(goVersion);
    }
    return new Promise<SemVersion>((resolve, reject) => {
        cp.execFile(goRuntimePath, ['version'], {}, (err, stdout, stderr) => {
            let matches = /go version go(\d).(\d).*/.exec(stdout);
            if (matches) {
                goVersion = {
                    major: parseInt(matches[1], 10),
                    minor: parseInt(matches[2], 10),
                };
            }
            return resolve(goVersion);
        });
    });
}

/**
 * Returns boolean denoting if current version of Go supports vendoring
 */
export function isVendorSupported(): Promise<boolean> {
    if (vendorSupport != null) {
        return Promise.resolve(vendorSupport);
    }
    return getGoVersion().then(version => {
        if (!version) {
            return process.env['GO15VENDOREXPERIMENT'] === '0' ? false : true;
        }

        switch (version.major) {
            case 0:
                vendorSupport = false;
                break;
            case 1:
                vendorSupport = (version.minor > 6 || ((version.minor === 5 || version.minor === 6) && process.env['GO15VENDOREXPERIMENT'] === '1')) ? true : false;
                break;
            default:
                vendorSupport = true;
                break;
        }
        return vendorSupport;
    });
}

export function isPositionInString(document, position): boolean {
    let lineText = document.lineAt(position.line).text;
    let lineTillCurrentPosition = lineText.substr(0, position.character);

    // Count the number of double quotes in the line till current position. Ignore escaped double quotes
    let doubleQuotesCnt = (lineTillCurrentPosition.match(/\"/g) || []).length;
    let escapedDoubleQuotesCnt = (lineTillCurrentPosition.match(/\\\"/g) || []).length;

    doubleQuotesCnt -= escapedDoubleQuotesCnt;
    return doubleQuotesCnt % 2 === 1;
}

export function getToolsGopath(useCache: boolean = true): string {
    if (!useCache || !toolsGopath) {
        toolsGopath = resolveToolsGopath();
    }

    return toolsGopath;
}

function resolveToolsGopath(): string {

    let toolsGopathForWorkspace = '';

    // In case of multi-root, resolve ~ and ignore ${workspaceRoot}
    if (toolsGopathForWorkspace.startsWith('~')) {
        toolsGopathForWorkspace = path.join(os.homedir(), toolsGopathForWorkspace.substr(1));
    }
    if (toolsGopathForWorkspace && toolsGopathForWorkspace.trim() && !/\${workspaceRoot}/.test(toolsGopathForWorkspace)) {
        return toolsGopathForWorkspace;
    }
}

export function getBinPath(tool: string): string {
    return getBinPathWithPreferredGopath(tool, getToolsGopath(), getCurrentGoPath());
}

export function getFileArchive(document): string {
    let fileContents = document.getText();
    return document.fileName + '\n' + Buffer.byteLength(fileContents, 'utf8') + '\n' + fileContents;
}

export function getToolsEnvVars(): any {
    const gopath = getCurrentGoPath();
    const envVars = Object.assign({}, process.env, gopath ? { GOPATH: gopath } : {});

    // cgo expects go to be in the path
    const goroot: string = envVars['GOROOT'];
    if (goroot && (<string>envVars['PATH'] || '').split(path.delimiter).indexOf(goroot) === -1) {
        envVars['PATH'] += (envVars['PATH'] ? path.delimiter : '') + goroot;
    }

    return envVars;
}

export function getCurrentGoPath(): string {
    return process.env['GOPATH'];
}

export class LineBuffer {
    private buf: string = '';
    private lineListeners: { (line: string): void; }[] = [];
    private lastListeners: { (last: string): void; }[] = [];

    public append(chunk: string) {
        this.buf += chunk;
        do {
            const idx = this.buf.indexOf('\n');
            if (idx === -1) {
                break;
            }

            this.fireLine(this.buf.substring(0, idx));
            this.buf = this.buf.substring(idx + 1);
        } while (true);
    }

    public done() {
        this.fireDone(this.buf !== '' ? this.buf : null);
    }

    private fireLine(line: string) {
        this.lineListeners.forEach(listener => listener(line));
    }

    private fireDone(last: string) {
        this.lastListeners.forEach(listener => listener(last));
    }

    public onLine(listener: (line: string) => void) {
        this.lineListeners.push(listener);
    }

    public onDone(listener: (last: string) => void) {
        this.lastListeners.push(listener);
    }
}

export function timeout(millis): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        setTimeout(() => resolve(), millis);
    });
}

/**
 * Exapnds ~ to homedir in non-Windows platform and resolves ${workspaceRoot}
 */
export function resolvePath(inputPath: string, workspaceRoot?: string): string {
    if (!inputPath || !inputPath.trim()) { return inputPath; }

    return resolveHomeDir(inputPath);
}

/**
 * Guess the package name based on parent directory name of the given file
 *
 * Cases:
 * - dir 'go-i18n' -> 'i18n'
 * - dir 'go-spew' -> 'spew'
 * - dir 'kingpin' -> 'kingpin'
 * - dir 'go-expand-tilde' -> 'tilde'
 * - dir 'gax-go' -> 'gax'
 * - dir 'go-difflib' -> 'difflib'
 * - dir 'jwt-go' -> 'jwt'
 * - dir 'go-radix' -> 'radix'
 *
 * @param {string} filePath.
 */
export function guessPackageNameFromFile(filePath): Promise<string[]> {
    return new Promise((resolve, reject) => {

        const goFilename = path.basename(filePath);
        if (goFilename === 'main.go') {
            return resolve(['main']);
        }

        const directoryPath = path.dirname(filePath);
        const dirName = path.basename(directoryPath);
        let segments = dirName.split(/[\.-]/);
        segments = segments.filter(val => val !== 'go');

        if (segments.length === 0 || !/[a-zA-Z_]\w*/.test(segments[segments.length - 1])) {
            return reject();
        }

        const proposedPkgName = segments[segments.length - 1];

        if (goFilename.endsWith('_test.go')) {
            return resolve([proposedPkgName, proposedPkgName + '_test']);
        }

        fs.stat(path.join(directoryPath, 'main.go'), (err, stats) => {
            if (stats && stats.isFile()) {
                return resolve(['main']);
            }
            return resolve([proposedPkgName]);
        });
    });
}

export class Range implements lsp.Range {
    public readonly start: lsp.Position;
    public readonly end: lsp.Position;

    constructor(range: lsp.Range)
    constructor(start: lsp.Position, end: lsp.Position)
    constructor(startLine: number, startCharacter: number, endLine: number, endCharacter: number)
    constructor(first: lsp.Position | number | lsp.Range, second?: lsp.Position | number,
        endLine: number = -1, endCharacter: number = -1) {
        if (lsp.Range.is(first)) {
            this.start = first.start;
            this.end = first.end;
        } else if (lsp.Position.is(first) && lsp.Position.is(second)) {
            this.start = first;
            this.end = second;
        } else {
            this.start = new Position(first as number, second as number);
            this.end = new Position(endLine, endCharacter);
        }
    }

    public static contains(range: lsp.Range, position: lsp.Position): boolean {
        return (range.start.line < position.line
            || (range.start.line === position.line && range.start.character <= position.character))
            && (range.end.line > position.line
                || (range.end.line === position.line && range.end.character >= position.character));
    }

    public isEmpty(): boolean {
        return this.start.line === this.end.line
            && this.start.character === this.end.character;
    }
}

export class Location implements lsp.Location {
    public uri: string;
    public range: lsp.Range;

    constructor(range: lsp.Range, uri: string)
    constructor(uri: Uri, range: lsp.Range)
    constructor(uri: Uri, position: lsp.Position)
    constructor(public readonly rangeOrUri: lsp.Range | Uri, public readonly uriRangeOrPosition: string | lsp.Range | lsp.Position) {
        if (lsp.Range.is(rangeOrUri)) {
            this.range = rangeOrUri;
        } else {
            this.uri = uriToStringUri(rangeOrUri);
        }
        if (lsp.Range.is(uriRangeOrPosition)) {
            this.range = uriRangeOrPosition;
        } else if (lsp.Position.is(uriRangeOrPosition)) {
            this.range = new Range(uriRangeOrPosition, uriRangeOrPosition);
        } else {
            this.uri = uriRangeOrPosition;
        }
    }
}

export class Position implements lsp.Position {
    constructor(public readonly line: number, public readonly character: number) { }
}

export class Hover implements lsp.Hover {
    constructor(public readonly contents: lsp.MarkedString | lsp.MarkedString[], public readonly range?: lsp.Range) { }
}

export class TextEdit implements lsp.TextEdit {
    constructor(public readonly range: lsp.Range, public readonly newText: string) { }

    public static insert(position: Position, text: string) {
        return new TextEdit(new Range(position, position), text);
    }

    public static delete(range: Range) {
        return new TextEdit(range, '');
    }

    public static replace(range: Range, text: string) {
        return new TextEdit(range, text);
    }
}

export class WorkspaceEdit implements lsp.WorkspaceEdit {
    public changes: {
        [uri: string]: TextEdit[];
    } = {};

    public set(uri: Uri, edits: TextEdit[]) {
        this.changes[uriToStringUri(uri)] = edits;
    }

    public add(uri: Uri, edit: TextEdit) {
        const existing = this.changes[uriToStringUri(uri)];
        if (existing) {
            existing.push(edit);
        } else {
            this.set(uri, [edit]);
        }
    }

    public insert(uri: Uri, position: Position, text: string) {
        this.add(uri, new TextEdit(new Range(position, position), text));
    }

    public delete(uri: Uri, range: Range) {
        this.add(uri, new TextEdit(range, ''));
    }

    public replace(uri: Uri, range: Range, text: string) {
        this.add(uri, new TextEdit(range, text));
    }
}

export class TextEditorEdit {

    public textEdits: lsp.TextEdit[] = [];

    public replace(location: Position | Range /*| Selection*/, newText: string): void {
        if (lsp.Position.is(location)) {
            this.textEdits.push(TextEdit.replace(new Range(location, location), newText));
        } else {
            this.textEdits.push(TextEdit.replace(location, newText));
        }
    }

    public insert(location: Position, newText: string): void {
        this.textEdits.push(TextEdit.insert(location, newText));

    }

    public delete(location: Range /*| Selection*/): void {
        this.textEdits.push(TextEdit.delete(location));
    }
}

export class SymbolInformation implements lsp.SymbolInformation {
    public uri: string;
    public location: lsp.Location | undefined;

    constructor(public readonly name: string,
        public readonly kind: lsp.SymbolKind,
        public readonly range: lsp.Range,
        uri: string | undefined,
        public readonly containerName?: string) {
        this.location = new Location(range, uri);
    }
}

export function uriToStringUri(uri: Uri) {
    return pathToUri(uri.fsPath);
}

export function pathToUri(p: string): string {
    return 'file://' + (isWindows() ? '/' + p.replace(/\//g, '/') : p);
}

export function isWindows(): boolean {
    return /^win/.test(process.platform);
}
